import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { EntityDataEffects } from '../entity-data/entity-data.effects';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../dialog/dialog.service';
import { FeaturesDataDispatcher } from '../features-data/features-data.dispatcher';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { NavigationService } from '../navigation/navigation.service';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { IState } from './expense-data.reducer';
import { ALL_LIST_FILTER } from '../entity-data/entity-data.data';
import { ExpenseDataBuilder } from './expense-data.builder';
import { ExpenseDataService } from './expense-data.service';
import { Expense } from './models/expense.model';
import { ExpenseDataStore } from './expense-data.store';
import { EMPTY, Observable, of } from 'rxjs';
import { concatMap, filter, map, switchMap, take } from 'rxjs/operators';
import { ActionsEnum as AppActionsEnum } from '../../store/app.actions';
import { List } from 'immutable';
import { SetCategories, setCategories } from './expense-data.actions';
import {
  ActionsEnum as EntityActionsEnum,
  SaveEntityNetworkSuccess,
} from '../entity-data/entity-data.actions';
import { AppStore } from '../../store';

@Injectable()
export class ExpenseDataEffects extends EntityDataEffects<IState, Expense> {
  constructor(
    private _expenseDataService: ExpenseDataService,
    expenseDataStore: ExpenseDataStore,
    expenseDataBuilder: ExpenseDataBuilder,
    actions: Actions,
    appStore: AppStore,
    translateService: TranslateService,
    globalBannerService: GlobalBannerService,
    dialogService: DialogService,
    navigationService: NavigationService,
    settingsDataStore: SettingsDataStore,
    featuresDataStore: FeaturesDataStore,
    localStorageService: LocalStorageService,
    featuresDataDispatcher: FeaturesDataDispatcher
  ) {
    super(
      actions,
      appStore,
      _expenseDataService,
      expenseDataStore,
      expenseDataBuilder,
      settingsDataStore,
      featuresDataStore,
      translateService,
      globalBannerService,
      dialogService,
      navigationService,
      localStorageService,
      featuresDataDispatcher
    );
  }

  @Effect() loadSearchParams = this.loadSearchParams;
  @Effect() persistSearchParams = this.persistSearchParams;
  @Effect() fetchList = this.fetchList;
  @Effect() searchList = this.searchList;
  @Effect() fetchNextPage = this.fetchNextPage;
  @Effect() fetchFilters = this.fetchFilters;
  @Effect() triggerRefreshFiltersWithDelay = this
    .triggerRefreshFiltersWithDelay;
  @Effect() refreshFiltersWithDelay = this.fetchFiltersWithDelay(
    ALL_LIST_FILTER
  );
  @Effect() getEntityById = this.getEntityById;
  @Effect() saveNewEntity = this.saveNewEntity;
  @Effect() saveNewEntityError = this.saveNewEntityError;
  @Effect() updateEntity = this.updateEntity;
  @Effect() createNewEditingEntity = this.createNewEditingEntity;
  @Effect() refreshEditingEntity = this.refreshEditingEntity;
  @Effect() setEditingEntityContent = this.setEditingEntityContent;
  @Effect() setEditingEntityProperties = this.setEditingEntityProperties;
  @Effect() setEditingEntityField = this.setEditingEntityField;
  @Effect() saveEditingEntity = this.saveEditingEntity;
  @Effect() saveExistingEntityError = this.saveExistingEntityError;
  @Effect() saveEntityStore = this.saveEntityStore;
  @Effect() deleteEntityStore = this.deleteEntityStore;
  @Effect() saveEntityToNetwork = this.saveEntityToNetwork;
  @Effect() deleteEntityNetwork = this.deleteEntityNetwork;
  @Effect() refreshListAndFiltersOnTaxYearChange = this
    .refreshListAndFiltersOnTaxYearChange;

  @Effect()
  fetchCategories: Observable<SetCategories> = this.actions.pipe(
    ofType(AppActionsEnum.INIT_APP),
    take(1),
    concatMap(() => {
      return this._expenseDataService
        .getCategories()
        .pipe(
          map((categories: List<string>) =>
            setCategories(this.entityType, categories)
          )
        );
    })
  );

  @Effect()
  saveEditingEntityCategory: Observable<SetCategories> = this.actions.pipe(
    ofType(EntityActionsEnum.SAVE_ENTITY_NETWORK_SUCCESS),
    filter(
      ({ payload }: SaveEntityNetworkSuccess) =>
        payload.entityType === this.entityType &&
        payload.sourceAction === EntityActionsEnum.SAVE_EDITING_ENTITY
    ),
    switchMap(({ payload }: SaveEntityNetworkSuccess) => {
      const expense = payload.entity as Expense;

      return expense.category
        ? of(setCategories(this.entityType, List([expense.category])))
        : EMPTY;
    })
  );
}
